// Czech public holidays ("státní svátky" + "ostatní svátky").
//
// On these days Prague's paid-parking zones (ZPS) are NOT enforced for the
// visitor/mixed regime — i.e. parking behaves as free — the same way it does on
// Sundays. (This mirrors the rule shown on the official signage and the
// parking.praha.eu rules pages: paid regime applies on working days / listed
// hours; public holidays are treated as non-charged.)
//
// All fixed-date Czech holidays plus the two movable Easter holidays are
// included. No external dependency: Easter is computed with the Anonymous
// Gregorian algorithm (Meeus/Jones/Butcher).

/** Compute Gregorian Easter Sunday for a year. @returns {{month:number, day:number}} (month 3 or 4) */
export function easterSunday(year) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return { month, day }
}

// Fixed-date Czech public holidays as "MM-DD".
const FIXED = new Set([
  '01-01', // Nový rok / Den obnovy samostatného českého státu
  '05-01', // Svátek práce
  '05-08', // Den vítězství
  '07-05', // Den slovanských věrozvěstů Cyrila a Metoděje
  '07-06', // Den upálení mistra Jana Husa
  '09-28', // Den české státnosti
  '10-28', // Den vzniku samostatného československého státu
  '11-17', // Den boje za svobodu a demokracii
  '12-24', // Štědrý den
  '12-25', // 1. svátek vánoční
  '12-26', // 2. svátek vánoční
])

const NAMES = {
  '01-01': 'Nový rok',
  '05-01': 'Svátek práce',
  '05-08': 'Den vítězství',
  '07-05': 'Cyril a Metoděj',
  '07-06': 'Jan Hus',
  '09-28': 'Den české státnosti',
  '10-28': 'Vznik Československa',
  '11-17': 'Den boje za svobodu',
  '12-24': 'Štědrý den',
  '12-25': '1. svátek vánoční',
  '12-26': '2. svátek vánoční',
}

const _movableCache = new Map()

function movableForYear(year) {
  if (_movableCache.has(year)) return _movableCache.get(year)
  const es = easterSunday(year)
  // Good Friday = Easter Sunday - 2 days; Easter Monday = + 1 day.
  const base = new Date(Date.UTC(year, es.month - 1, es.day))
  const goodFriday = new Date(base.getTime() - 2 * 86400000)
  const easterMonday = new Date(base.getTime() + 1 * 86400000)
  const key = (dt) =>
    `${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`
  const map = new Map([
    [key(goodFriday), 'Velký pátek'],
    [key(easterMonday), 'Velikonoční pondělí'],
  ])
  _movableCache.set(year, map)
  return map
}

/**
 * Is the given local date a Czech public holiday?
 * @param {number} year @param {number} month 1-12 @param {number} day 1-31
 * @returns {boolean}
 */
export function isCzechHoliday(year, month, day) {
  const mmdd = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  if (FIXED.has(mmdd)) return true
  return movableForYear(year).has(mmdd)
}

/** Human-readable holiday name, or null. */
export function czechHolidayName(year, month, day) {
  const mmdd = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  if (NAMES[mmdd]) return NAMES[mmdd]
  return movableForYear(year).get(mmdd) || null
}
